import { useCallback } from 'react';
import { useCreate, useNotify, useRedirect } from 'react-admin';

import { CMSAPIResponseBody, getFieldSpecificErrors } from '@app/services/cms/responses';
import { FIELD_TYPES, validateFormFields } from '@app/utils/validateUtils';

import { GenericObject } from '@app/components/common/Table/DefaultTable/types';
import CreateView from '@app/components/common/views/CreateView';

import { CreateUserForm } from './CreateUserForm';

const formFields = [
  { key: 'name', type: FIELD_TYPES.MULTILINGUAL, message: 'Name is required' },
  { key: 'role', type: FIELD_TYPES.SELECT, message: 'Role is required' },
  { key: 'email', type: FIELD_TYPES.EMAIL, message: 'A valid email address is required' },
  { key: 'password', type: FIELD_TYPES.PASSWORD, message: 'Password is required' },
  { key: 'confirm_password', type: FIELD_TYPES.CONFIRM_PASSWORD, message: 'Please confirm the password' },
];

export const CreateUser = () => {
  const validate = useCallback(
    (values: GenericObject) => validateFormFields({ fields: formFields, values, image: undefined }),
    []
  );

  const notify = useNotify();

  const redirect = useRedirect();
  const [create] = useCreate();
  const save = useCallback(
    async (values: any) => {
      try {
        await create('users', { data: values }, { returnPromise: true });
        notify('User created', {
          type: 'info',
          messageArgs: { smart_count: 1 },
        });
        redirect('/users');
      } catch (error: any) {
        const response = error.body as CMSAPIResponseBody;
        if (response.errors) {
          // Here, `password` corresponds to
          // `requiredFields` key
          let fields = formFields.map((field) => field.key);
          const { fieldSpecificErrorFound, fieldSpecificErrors } = getFieldSpecificErrors(fields, response);
          if (fieldSpecificErrorFound) {
            return fieldSpecificErrors;
          } else {
            // Some non-field specific error happened.
            notify(response.errors!.join('\n'), {
              type: 'error',
              messageArgs: { smart_count: 1 },
            });
          }
        } else {
          notify('An unknown error occurred while attempting to create a user, please try again later', {
            type: 'error',
            messageArgs: { smart_count: 1 },
          });
        }
      }
    },
    [create, notify, redirect]
  );

  return (
    <CreateView resourceSingular="user" validate={validate} onSubmit={save} data-cy="CreateUser">
      <CreateUserForm />
    </CreateView>
  );
};
